// 📁 src/layouts/DesignSample/data/DesignSampleManagerTable.js
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Swal from "sweetalert2";
import SoftBox from "components/SoftBox";
import Table from "examples/Tables/Table";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import { fetchDesignSampleDelete, fetchDesignSampleToggleStatus } from "api/designSampleService";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { getImageUrl } from "api/AttachmentAPI";
import ImageCustom from "components/ImageCustom/ImageCustom";
import AddDesignSample from "./AddDesignSample";

function DesignSampleManagerTable({
  data,
  totalElements,
  curPage,
  setCurPage,
  rowsCount,
  setRowsCount,
  refreshDataAfterChange,
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [imageMap, setImageMap] = useState({});

  useEffect(() => {
    const fetchImages = async () => {
      const fetchTasks = data.map((item) => {
        if (item.attachment?.encryptedId) {
          return getImageUrl(`/api/attachments/downloadFile/${item.attachment.encryptedId}`).then(
            (url) => ({
              encryptedId: item.attachment.encryptedId,
              url,
            })
          );
        }
        return null;
      });

      const results = await Promise.all(fetchTasks);

      const newMap = {};
      results.forEach((res) => {
        if (res?.url) {
          newMap[res.encryptedId] = res.url;
        }
      });
      setImageMap(newMap);
    };

    if (data.length > 0) fetchImages();
  }, [data]);

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsAddOpen(true);
  };

  const handleToggleStatus = async (item) => {
    const result = await fetchDesignSampleToggleStatus(item.designSampleId);
    if (result.success) {
      Swal.fire({
        icon: "success",
        title: "Cập nhật trạng thái thành công",
        timer: 1500,
        showConfirmButton: false,
      });
      refreshDataAfterChange();
    } else {
      Swal.fire({
        icon: "error",
        title: "Lỗi",
        text: result.data?.message || "Không thể cập nhật trạng thái",
      });
    }
  };

  const handleDelete = async (item) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Bạn chắc chắn muốn xoá?",
      text: `Mẫu thiết kế "${item.designSampleName}" sẽ bị xoá!`,
      showCancelButton: true,
      confirmButtonText: "Xoá",
      cancelButtonText: "Huỷ",
    });

    if (confirm.isConfirmed) {
      const result = await fetchDesignSampleDelete(item.designSampleId);
      if (result.success) {
        Swal.fire({
          icon: "success",
          title: "Xoá thành công!",
          timer: 1500,
          showConfirmButton: false,
        });
        refreshDataAfterChange();
      } else {
        Swal.fire({
          icon: "error",
          title: "Lỗi",
          text: result.data?.message || "Không thể xoá mẫu thiết kế",
        });
      }
    }
  };

  const columns = [
    { name: "Tên mẫu", align: "center", alignHeader: "center", sortable: true },
    { name: "Ngày tạo", align: "center", alignHeader: "center", sortable: true },
    { name: "Danh mục sản phẩm", align: "center", alignHeader: "center", sortable: true },
    { name: "Sắp xếp thứ tự hiển thị", align: "center", alignHeader: "center", sortable: true },
    { name: "Chức năng", align: "center", alignHeader: "center" },
    { name: "Hình ảnh", align: "center", alignHeader: "center" },
  ];

  const rows = data.map((item) => ({
    "Tên mẫu": item.designSampleName || "---",
    "Ngày tạo": moment(item.createTime).format("DD/MM/YYYY"),
    "Danh mục sản phẩm": item.templateName || "---",
    "Sắp xếp thứ tự hiển thị": (
      <SoftBox
        sx={{
          backgroundColor: "#F59F00",
          color: "white !important",
          fontWeight: "bold",
          borderRadius: "8px",
          padding: "2px 10px",
          display: "inline-block",
          textAlign: "center",
          minWidth: "24px",
        }}
      >
        {item.displayOrder || "-"}
      </SoftBox>
    ),
    "Chức năng": (
      <SoftBox display="flex" justifyContent="center" gap={1}>
        <Switch
          title={item.status === 1 ? "Kích hoạt" : "Tắt"}
          checked={item.status === 1}
          onClick={(e) => e.stopPropagation()}
          onChange={() => handleToggleStatus(item)}
        />
        <IconButton
          sx={{ color: "red" }}
          title="Xóa"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(item);
          }}
        >
          <DeleteIcon />
        </IconButton>
        <IconButton
          sx={{ color: "green" }}
          title="Sửa"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(item);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </SoftBox>
    ),
    "Hình ảnh": <ImageCustom url={imageMap[item.attachment?.encryptedId]} />,
  }));

  const handleCloseDialog = () => {
    setIsAddOpen(false);
    setSelectedItem(null);
  };

  return (
    <SoftBox>
      <Table
        columns={columns}
        rows={rows}
        isPagination={true}
        totalElements={totalElements}
        rowsPerPageOptions={[5, 10, 20, 50]}
        rowsCount={rowsCount}
        setRowsCount={setRowsCount}
        curPage={curPage}
        setCurPage={setCurPage}
        isRowSelectable={true}
        isDisabledSingleRow={() => false}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
      <AddDesignSample
        open={isAddOpen}
        onClose={handleCloseDialog}
        editMode={!!selectedItem}
        initialData={selectedItem}
        refreshDataAfterChange={refreshDataAfterChange}
      />
    </SoftBox>
  );
}

DesignSampleManagerTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalElements: PropTypes.number.isRequired,
  curPage: PropTypes.number.isRequired,
  setCurPage: PropTypes.func.isRequired,
  rowsCount: PropTypes.number.isRequired,
  setRowsCount: PropTypes.func.isRequired,
  refreshDataAfterChange: PropTypes.func.isRequired,
};

export default DesignSampleManagerTable;
